import React, { useState } from "react";
import { api, fmtError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import CurrencyPicker from "@/components/CurrencyPicker";
import { PRICING_MODELS, CREATIVE_TYPES } from "@/pages/CampaignCreate";
import { toast } from "sonner";

/**
 * Modal to edit a campaign template.
 * Used both inside /campaigns/new picker and on /templates library page.
 */
export default function EditTemplateModal({ template, clients = [], onClose, onSaved }) {
    const [name, setName] = useState(template.name || "");
    const [description, setDescription] = useState(template.description || "");
    const [snapshot, setSnapshot] = useState({
        client_org_id: template.snapshot?.client_org_id || "",
        volume: template.snapshot?.volume ?? "",
        payout: template.snapshot?.payout ?? "",
        revenue: template.snapshot?.revenue ?? "",
        currency: template.snapshot?.currency || "USD",
        targeting: template.snapshot?.targeting || "",
        creative_type: template.snapshot?.creative_type || "Display",
        pricing_model: template.snapshot?.pricing_model || "CPM",
        notes: template.snapshot?.notes || "",
    });
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!name.trim()) {
            toast.error("Template name cannot be empty.");
            return;
        }
        setBusy(true);
        try {
            await api.put(`/campaign-templates/${template.id}`, {
                name: name.trim(),
                description: description.trim() || null,
                snapshot: {
                    ...snapshot,
                    volume: snapshot.volume === "" ? null : Number(snapshot.volume),
                    payout: snapshot.payout === "" ? null : Number(snapshot.payout),
                    revenue: snapshot.revenue === "" ? null : Number(snapshot.revenue),
                },
            });
            onSaved?.();
        } catch (err) {
            toast.error(fmtError(err.response?.data?.detail) || err.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="edit-template-modal">
            <form onSubmit={submit} className="bg-white border border-slate-200 max-w-2xl w-full mt-12 mb-12">
                <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
                    <div>
                        <div className="overline">Edit</div>
                        <h3 className="font-display text-lg">Campaign template</h3>
                    </div>
                    <button type="button" onClick={onClose} className="text-2xl leading-none text-slate-500" data-testid="edit-template-close">×</button>
                </div>
                <div className="p-6 space-y-4">
                    <Field label="Template name">
                        <Input required value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-template-name" className="rounded-none border-slate-300" />
                    </Field>
                    <Field label="Description (optional)" full>
                        <Textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={2}
                            data-testid="edit-template-description"
                            className="rounded-none border-slate-300"
                            placeholder="When to use this template, any special caveats…"
                        />
                    </Field>
                    <div className="overline pt-2">Snapshot defaults</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Client">
                            <Select value={snapshot.client_org_id} onValueChange={(v) => setSnapshot({ ...snapshot, client_org_id: v })}>
                                <SelectTrigger data-testid="edit-template-client" className="rounded-none h-10 border-slate-300"><SelectValue placeholder="Any client" /></SelectTrigger>
                                <SelectContent>
                                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Currency">
                            <CurrencyPicker value={snapshot.currency} onChange={(v) => setSnapshot({ ...snapshot, currency: v })} testid="edit-template-currency" />
                        </Field>
                        <Field label="Volume">
                            <Input type="number" value={snapshot.volume} onChange={(e) => setSnapshot({ ...snapshot, volume: e.target.value })} className="rounded-none border-slate-300 font-mono" />
                        </Field>
                        <Field label="Payout">
                            <Input type="number" value={snapshot.payout} onChange={(e) => setSnapshot({ ...snapshot, payout: e.target.value })} className="rounded-none border-slate-300 font-mono" />
                        </Field>
                        <Field label="Revenue">
                            <Input type="number" value={snapshot.revenue} onChange={(e) => setSnapshot({ ...snapshot, revenue: e.target.value })} className="rounded-none border-slate-300 font-mono" />
                        </Field>
                        <Field label="Pricing model">
                            <Select value={snapshot.pricing_model} onValueChange={(v) => setSnapshot({ ...snapshot, pricing_model: v })}>
                                <SelectTrigger className="rounded-none h-10 border-slate-300"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {PRICING_MODELS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Creative type">
                            <Select value={snapshot.creative_type} onValueChange={(v) => setSnapshot({ ...snapshot, creative_type: v })}>
                                <SelectTrigger className="rounded-none h-10 border-slate-300"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {CREATIVE_TYPES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Targeting" full>
                            <Textarea value={snapshot.targeting} onChange={(e) => setSnapshot({ ...snapshot, targeting: e.target.value })} rows={2} className="rounded-none border-slate-300" />
                        </Field>
                        <Field label="Notes" full>
                            <Textarea value={snapshot.notes} onChange={(e) => setSnapshot({ ...snapshot, notes: e.target.value })} rows={2} className="rounded-none border-slate-300" />
                        </Field>
                    </div>
                </div>
                <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={onClose} className="rounded-none border-slate-300">Cancel</Button>
                    <Button type="submit" disabled={busy} data-testid="edit-template-submit" className="rounded-none bg-slate-900 hover:bg-slate-800 text-white px-6">
                        {busy ? "Saving…" : "Save changes"}
                    </Button>
                </div>
            </form>
        </div>
    );
}

function Field({ label, children, full = false }) {
    return (
        <div className={full ? "md:col-span-2" : ""}>
            <Label className="overline mb-1.5 block">{label}</Label>
            {children}
        </div>
    );
}
